import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { toast } from '../../../../components/ui/toaster';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { LooseRelationship } from '../../../../types/electron';
import { buildInboundForeignKeyFilters, getInboundForeignKeyMappings } from '../utils/inboundForeignKeyUtils';
import { openTableExplorerTab } from '../utils/openTableExplorerTab';
import { InboundForeignKeyRowsPreview } from './InboundForeignKeyRowsPreview';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { useMetaInfo } from '../../common/hooks/useMetaInfo';
import { DrillOpenInMenu } from '../../common/components/DrillOpenInMenu';
import type { DatabaseEngineId } from '../../../../core/engines';

type InboundForeignKeyExplorerProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly parentRow: Record<string, unknown>;
  readonly foreignKeys: TableExplorerInboundForeignKey[];
  /** Engine of the active connection — plumbed through to the ⋯ submenu so the
   * QE fallback emits the correct dialect. Defaults to 'mssql' for legacy
   * callers that haven't been updated yet. */
  readonly engineId?: DatabaseEngineId;
  /**
   * Slice 3 (EP5 parity) — primary "Open" handler. When supplied, the button
   * drills into the active TE tab's navigation tree (push a filtered
   * table-node) rather than opening a new TE tab. Falls back to today's
   * new-tab behavior when undefined.
   */
  readonly onDrillInboundFk?: (args: {
    foreignKey: TableExplorerInboundForeignKey;
    looseRel: LooseRelationship | undefined;
  }) => void;
};

/** Extract the loose FK id from a constraintName like "loose:{id}". Returns null for real FKs. */
function parseLooseId(constraintName: string): string | null {
  if (!constraintName.startsWith('loose:')) return null;
  return constraintName.slice('loose:'.length);
}

/** Look up the LooseRelationship for a loose inbound FK, or undefined if not found / not loose. */
function resolveLooseRel(
  fk: TableExplorerInboundForeignKey,
  looseRelationships: readonly LooseRelationship[],
): LooseRelationship | undefined {
  if (!fk.isLoose) return undefined;
  const looseId = parseLooseId(fk.constraintName);
  if (!looseId) return undefined;
  return looseRelationships.find((r) => r.id === looseId);
}

export function InboundForeignKeyExplorer({
  connectionId,
  database,
  parentRow,
  foreignKeys,
  engineId = 'mssql',
  onDrillInboundFk,
}: InboundForeignKeyExplorerProps) {
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const { track } = useAnalytics();
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const looseRelationships = metaInfo?.looseRelationships ?? [];

  const sortedForeignKeys = useMemo(() => {
    return [...foreignKeys].sort((a, b) => {
      // Real FKs sort before loose ones.
      const aLoose = a.isLoose ? 1 : 0;
      const bLoose = b.isLoose ? 1 : 0;
      if (aLoose !== bLoose) return aLoose - bLoose;
      const aName = `${a.referencingSchema}.${a.referencingTable}.${a.constraintName}`;
      const bName = `${b.referencingSchema}.${b.referencingTable}.${b.constraintName}`;
      return aName.localeCompare(bName);
    });
  }, [foreignKeys]);

  const toggleExpanded = useCallback((id: string, fk: TableExplorerInboundForeignKey) => {
    setExpandedIds((prev) => {
      const expanding = !prev.includes(id);
      if (expanding) {
        track('feature:row_related_data_expanded', { isLoose: !!fk.isLoose });
      }
      return expanding ? [...prev, id] : prev.filter((x) => x !== id);
    });
  }, [track]);

  const handleOpen = useCallback(
    (fk: TableExplorerInboundForeignKey) => {
      const looseRel = resolveLooseRel(fk, looseRelationships);
      const build = buildInboundForeignKeyFilters({ foreignKey: fk, parentRow, looseRel });
      if (!build.filters) {
        toast.error({
          title: 'FK navigation failed',
          description: `Missing value(s) for: ${build.missingColumns.join(', ')}`,
        });
        return;
      }

      // Slice 3 (EP5 parity): when the parent supplies a drill handler, push
      // a filtered table-node onto the navigation tree instead of opening a
      // new TE tab. The ⋯ menu still exposes "Open in new explorer" / "Open
      // in query editor" as secondary actions.
      if (onDrillInboundFk) {
        onDrillInboundFk({ foreignKey: fk, looseRel });
        return;
      }

      const title = `${fk.referencingSchema ? `${fk.referencingSchema}.` : ''}${fk.referencingTable} [FK]`;
      openTableExplorerTab({
        connectionId,
        database,
        schema: fk.referencingSchema,
        tableName: fk.referencingTable,
        title,
        filters: build.filters,
      });
    },
    [connectionId, database, parentRow, looseRelationships, onDrillInboundFk]
  );

  return (
    <div className="h-full flex flex-col overflow-hidden rounded-md border border-border bg-background/60">
      <div className="px-2 py-1.5 border-b border-border bg-muted/30 text-xs font-medium text-muted-foreground">
        Referenced by ({foreignKeys.length})
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-2">
        {sortedForeignKeys.map((fk) => {
          const id = `${fk.constraintName}|${fk.referencingSchema}|${fk.referencingTable}`;
          const isExpanded = expandedIds.includes(id);
          const mappings = getInboundForeignKeyMappings(fk);
          const mappingLabel = mappings.length
            ? mappings.map((m) => `${m.referencingColumn} → ${m.referencedColumn}`).join(', ')
            : 'Unknown mapping';

          return (
            // PP-30 — no border-in-border. The outer container at the top of
            // this component is already `rounded-md border border-border`, so a
            // per-item box drew a second frame inside the first. Same treatment
            // PP-30 applied in `single-row/` and T5.5's fix round applied to
            // `RowDetailsDataTab` — the sibling "Data" tab of THIS very drawer,
            // which is why leaving this one boxed showed a user two different
            // list treatments while switching tabs inside one panel. The outer
            // container stays the sole bounding border; items separate on a
            // `--divider` hairline.
            <div
              key={id}
              className="border-b border-divider"
              data-testid={`inbound-fk-card-${fk.constraintName}`}
            >
              <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                <button
                  type="button"
                  // T6.1c — was ringless, so its focus ring fell through to the
                  // UA's `-webkit-focus-ring-color`, i.e. the macOS system
                  // accent. `src/index.css`'s `:focus-visible` backstop now
                  // recolours that class app-wide; this site additionally takes
                  // the explicit `button.tsx` treatment because a disclosure
                  // toggle is a primary keyboard affordance and should carry
                  // the same ring as every other button, not just a recoloured
                  // UA default.
                  className="flex items-center gap-2 min-w-0 text-left rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                  onClick={() => toggleExpanded(id, fk)}
                  title="Toggle preview"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate flex items-center">
                      <span className="truncate">
                        {fk.referencingSchema ? `${fk.referencingSchema}.` : ''}
                        {fk.referencingTable}
                      </span>
                      {fk.isLoose && (
                        <span
                          className="inline-flex items-center gap-0.5 ml-2 px-1.5 py-0.5 text-[10px] rounded border border-teal-500/30 bg-teal-500/10 text-teal-500"
                          title="Loose relationship — declared by you, not enforced by the database."
                          data-testid={`loose-fk-inbound-badge-${fk.constraintName}`}
                        >
                          Loose
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{mappingLabel}</div>
                  </div>
                </button>

                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => handleOpen(fk)}
                    title="Open in Table Explorer"
                  >
                    <Maximize2 className="h-3.5 w-3.5 mr-1" />
                    Open
                  </Button>
                  <DrillOpenInMenu
                    connectionId={connectionId}
                    database={database}
                    engineId={engineId}
                    source="related-section-menu"
                    buttonTestId={`inbound-fk-${fk.constraintName}-more-menu`}
                    target={{
                      kind: 'table',
                      schema: fk.referencingSchema,
                      table: fk.referencingTable,
                    }}
                  />
                </div>
              </div>

              {isExpanded && (
                <InboundForeignKeyRowsPreview
                  connectionId={connectionId}
                  database={database}
                  foreignKey={fk}
                  parentRow={parentRow}
                  looseRel={resolveLooseRel(fk, looseRelationships)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
